import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { L } from "../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../constants/trial";
import { LEGAL_PATHS } from "../../../constants/legalPaths";
import { LEGAL_OPERATOR } from "../../../constants/legalDocuments";
import {
  DIRECTION_LINKS,
  FOOTER_COLS,
  IMG,
  NAV_FEATURES,
  PATHS,
} from "./nav";
import s from "./site.module.css";

const MOBILE_LINKS = [
  [PATHS.features, "Возможности"],
  [PATHS.pricing, "Тарифы"],
  [PATHS.connect, "Подключение"],
  [PATHS.about, "О компании"],
];

function Arrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M5 13L13 5M13 5H7M13 5V11"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Btn({ to = "#register", children, className = "", variant }) {
  const cls = [
    s.btn,
    variant === "ghost" ? s.btnGhost : "",
    variant === "dark" ? s.btnDark : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const inner = (
    <>
      {children}
      {variant !== "ghost" ? <Arrow /> : null}
    </>
  );
  if (typeof to === "string" && to.startsWith("#")) {
    return (
      <a href={to} className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  );
}

export default function SiteShell({ children }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
    let id = "";
    try {
      id = location.hash ? decodeURIComponent(location.hash.slice(1)) : "";
    } catch {
      id = "";
    }
    if (!id) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const frame = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView();
      else window.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(frame);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!navRef.current?.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  const closeOnLink = (e) => {
    if (e.target.closest("a")) setMobileOpen(false);
  };

  return (
    <div className={`${s.shell} ${mobileOpen ? s.mobileOpen : ""}`}>
      <header className={s.nav} ref={navRef}>
        <div className={s.navInner}>
          <Link to={PATHS.home} className={s.brand}>
            <img src={IMG.logo} alt="" width={32} height={32} />
            <span>{L.productName}</span>
          </Link>

          <nav className={s.navMenus} aria-label="Разделы сайта">
            <div
              className={`${s.navItem} ${openMenu === "audience" ? s.navItemOpen : ""}`}
            >
              <button
                type="button"
                className={s.navTrigger}
                aria-expanded={openMenu === "audience"}
                onClick={() =>
                  setOpenMenu((v) => (v === "audience" ? null : "audience"))
                }
              >
                Для кого <i className={s.chevron} aria-hidden />
              </button>
              <div className={`${s.dropdown} ${s.dropAudience}`}>
                <p className={s.audTagsHead}>Популярные направления</p>
                <div className={s.audOrg}>
                  {DIRECTION_LINKS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={s.dropLink}
                      onClick={() => setOpenMenu(null)}
                    >
                      <strong>{item.title}</strong>
                      <span>{item.text}</span>
                    </Link>
                  ))}
                </div>
                <div className={s.audFoot}>
                  <Link
                    to={PATHS.audience}
                    className={s.audCta}
                    onClick={() => setOpenMenu(null)}
                  >
                    <strong>Подходит для любого вида спорта</strong>
                    <span>
                      Клубы, студии, школы, секции и персональные тренеры —
                      смотрите все сценарии.
                    </span>
                  </Link>
                </div>
              </div>
            </div>

            <div
              className={`${s.navItem} ${openMenu === "features" ? s.navItemOpen : ""}`}
            >
              <button
                type="button"
                className={s.navTrigger}
                aria-expanded={openMenu === "features"}
                onClick={() =>
                  setOpenMenu((v) => (v === "features" ? null : "features"))
                }
              >
                Возможности <i className={s.chevron} aria-hidden />
              </button>
              <div className={s.dropdown}>
                {NAV_FEATURES.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={s.dropLink}
                    onClick={() => setOpenMenu(null)}
                  >
                    <strong>{item.title}</strong>
                    <span>{item.text}</span>
                  </Link>
                ))}
                <Link
                  to={PATHS.features}
                  className={s.dropLink}
                  onClick={() => setOpenMenu(null)}
                >
                  <strong>Все возможности</strong>
                  <span>Полный обзор сервиса</span>
                </Link>
              </div>
            </div>

            <NavLink
              to={PATHS.pricing}
              className={({ isActive }) =>
                `${s.navLink} ${isActive ? s.navLinkOn : ""}`
              }
            >
              Тарифы
            </NavLink>
            <NavLink
              to={PATHS.connect}
              className={({ isActive }) =>
                `${s.navLink} ${isActive ? s.navLinkOn : ""}`
              }
            >
              Подключение
            </NavLink>
            <NavLink
              to={PATHS.about}
              className={({ isActive }) =>
                `${s.navLink} ${isActive ? s.navLinkOn : ""}`
              }
            >
              О компании
            </NavLink>
          </nav>

          <div className={s.navActions}>
            <Link to="/account/login" className={`${s.btn} ${s.btnGhost}`}>
              Войти
            </Link>
            <Btn>Создать клуб</Btn>
            <button
              type="button"
              className={s.burger}
              aria-label={mobileOpen ? "Закрыть меню" : "Меню"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              <i className={s.burgerLine} aria-hidden />
              <i className={s.burgerLine} aria-hidden />
              <i className={s.burgerLine} aria-hidden />
            </button>
          </div>
        </div>

        <div className={s.mobilePanel} onClick={closeOnLink}>
          <div className={s.mobilePanelScroll}>
            <p className={s.mobileKicker}>Для кого</p>
            <div className={s.audMobile}>
              <div className={s.audOrg}>
                {DIRECTION_LINKS.map((item) => (
                  <Link key={item.to} to={item.to} className={s.dropLink}>
                    <strong>{item.title}</strong>
                    <span>{item.text}</span>
                  </Link>
                ))}
              </div>
              <Link to={PATHS.audience} className={s.audCta}>
                <strong>Подходит для любого вида спорта</strong>
                <span>
                  Клубы, студии, школы, секции и персональные тренеры —
                  смотрите все сценарии.
                </span>
              </Link>
            </div>
            <nav className={s.mobileLinks} aria-label="Разделы сайта">
              {MOBILE_LINKS.map(([to, label]) => (
                <NavLink
                  key={to}
                  to={to}
                  end
                  className={({ isActive }) =>
                    `${s.mobileLink} ${isActive ? s.mobileLinkOn : ""}`
                  }
                >
                  {label}
                  <Arrow />
                </NavLink>
              ))}
            </nav>
          </div>
          <div className={s.mobileActions}>
            <Link to="/account/login" className={`${s.btn} ${s.btnGhost}`}>
              Войти
            </Link>
            <Btn>Создать клуб</Btn>
          </div>
        </div>
      </header>

      <main className={s.main}>{children}</main>

      <footer className={s.footer}>
        <div className={s.footerInner}>
          <div className={s.footerCta}>
            <div className={s.footerCtaCopy}>
              <p className={s.footerKicker}>Начните сегодня</p>
              <h2>
                Пусть клуб живёт спортом.
                <br />
                Рутину возьмёт {L.productName}.
              </h2>
              <ul className={s.footerTicks}>
                <li>{TRIAL_DAYS} дней полного доступа</li>
                <li>Без привязки карты</li>
                <li>Запуск за вечер</li>
              </ul>
              <div className={s.footerCtaActions}>
                <Btn className={`${s.btnLg} ${s.footerBtn}`}>Создать клуб</Btn>
                <Link to={PATHS.pricing} className={s.footerCtaLink}>
                  Сравнить тарифы ↗
                </Link>
              </div>
            </div>
            <div className={s.footerPhones} aria-hidden>
              <img src="/landing/mock-client-home.png" alt="" loading="lazy" />
              <img src="/landing/mock-brand-club.png" alt="" loading="lazy" />
            </div>
          </div>

          <div className={s.footerGrid}>
            <div className={s.footerAbout}>
              <Link to={PATHS.home} className={s.footerBrand}>
                <img src={IMG.logo} alt="" width={36} height={36} />
                <span>{L.productName}</span>
              </Link>
              <p className={s.footerText}>
                Расписание, журнал, абонементы и приложение клуба — сервис для
                секций, студий и спортивных школ.
              </p>
              <a
                href={`mailto:${LEGAL_OPERATOR.supportEmail}`}
                className={s.footerMail}
              >
                {LEGAL_OPERATOR.supportEmail}
              </a>
              <small>Поддержка и помощь с запуском</small>
            </div>
            {FOOTER_COLS.map((col) => (
              <div key={col.title} className={s.footerCol}>
                <h4>{col.title}</h4>
                {col.links.map((link) =>
                  link.href ? (
                    <a key={link.label} href={link.href}>
                      {link.label}
                    </a>
                  ) : (
                    <Link key={link.label} to={link.to}>
                      {link.label}
                    </Link>
                  ),
                )}
              </div>
            ))}
          </div>

          <div className={s.footerBottom}>
            <span>
              © {new Date().getFullYear()} {L.productName} ·{" "}
              {LEGAL_OPERATOR.legalName} · ИНН {LEGAL_OPERATOR.inn}
            </span>
            <nav className={s.footerLegal} aria-label="Документы">
              <Link to={LEGAL_PATHS.offer}>Оферта</Link>
              <Link to={LEGAL_PATHS.privacy}>Конфиденциальность</Link>
              <Link to={LEGAL_PATHS.cookies}>Cookies</Link>
              <Link to={LEGAL_PATHS.payments}>Оплата и возврат</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
